// src/utils/ledgerView.js
// P4-8 Group 2 (31 Oct 2026) - what the Inventory Ledger page shows and searches.
//
// WHY THIS FILE EXISTS
// A ledger row keeps the item code it was written with, and P4-5 gave items new
// codes, so the row's code can be an old one. Its reference_id is usually a
// Firestore doc id (a GRN row holds the PO's doc id), so "PO-2026-0063" could not
// be found. These helpers add the CURRENT code from Item Master and the PO number
// for display and search only. Nothing here touches the balance: the page runs
// withStoreBalance() on the FULL list first, so a filtered row keeps its true
// balance.
//
// Item lookup = item_id only, through matchItem(): it never guesses by code or
// name. Ambiguous or not found keeps the row's own code.
// Pure (no Firebase) -> covered by src/utils/ledgerView.test.js (Jest).

import { matchItem } from "./itemMatch";

const clean = (v) => String(v ?? "").trim();

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const twoDigits = (n) => String(n).padStart(2, "0");

/** @returns {string} "31 Oct 26, 14:05", or "-" with no timestamp */
export const ledgerShortDate = (timestamp) => {
  if (!timestamp?.seconds) return "-";
  const d = new Date(timestamp.seconds * 1000);
  return `${twoDigits(d.getDate())} ${MONTHS[d.getMonth()]} ${twoDigits(d.getFullYear() % 100)}, ` +
    `${twoDigits(d.getHours())}:${twoDigits(d.getMinutes())}`;
};

/**
 * Adds current_code, old_code (the row's code when it differs) and master_name.
 * @param {Array} rows   ledger rows
 * @param {Array} items  Item Master docs ({ id, item_id, item_code, item_name, is_active })
 * @returns {Array} new row objects, same order
 */
export const withCurrentItem = (rows = [], items = []) => {
  const masterById = new Map();
  const masterOf = (id) => {
    if (!id) return null;
    if (!masterById.has(id)) masterById.set(id, matchItem(items, { item_id: id }).item);
    return masterById.get(id);
  };

  return rows.map((row) => {
    const master = masterOf(clean(row?.item_id));
    const rowCode = clean(row?.item_code);
    const currentCode = clean(master?.item_code);
    return {
      ...row,
      master_name:  clean(master?.item_name),
      current_code: currentCode || rowCode,
      old_code:     currentCode && rowCode && rowCode !== currentCode ? rowCode : "",
    };
  });
};

/**
 * Adds reference_no: the PO number when reference_id is a PO's doc id, otherwise
 * reference_id as saved.
 * @param {Array} rows
 * @param {Map}   poNumberById  purchase_orders doc id -> po_number
 */
export const withReferenceNo = (rows = [], poNumberById = new Map()) =>
  rows.map((row) => {
    const id = clean(row?.reference_id);
    return { ...row, reference_no: poNumberById.get(id) || id };
  });

// P4-8 follow-up (a): "ITEM-0001" must not also list ITEM-00010 … 00019. Returns a
// row test when the text is exactly ONE item's item_id or current code, otherwise
// null = contains search. An old code is never exact: many items shared it before
// P4-5, and a shared "" item can still hold it as its current code. The item count
// is checked because a failed items read leaves the row's own, maybe shared, code
// in current_code.
const exactItemMatch = (rows, q) => {
  const same = (value) => clean(value).toLowerCase() === q;
  if (rows.some((row) => same(row.item_id))) return (row) => same(row.item_id);
  if (rows.some((row) => same(row.old_code))) return null;
  const itemIds = new Set(rows.filter((row) => same(row.current_code)).map((row) => clean(row.item_id)));
  return itemIds.size === 1 ? (row) => same(row.current_code) : null;
};

/**
 * @param {Array}  rows  rows that already went through withStoreBalance()
 * @param {Object} filter
 * @param {string} filter.text  item name, current or old code, item_id, reference id / PO number.
 *                              An exact item_id or current code shows only that item.
 * @param {"all"|"store"|"site"} filter.kind  ledger_kind; junk rows show under "all" only
 */
export const searchLedger = (rows = [], { text = "", kind = "all" } = {}) => {
  const q = clean(text).toLowerCase();
  // On every row, before the chip: a chip must not turn an exact search into a contains search.
  const isExactItem = q ? exactItemMatch(rows, q) : null;
  return rows.filter((row) => {
    if (kind !== "all" && row.ledger_kind !== kind) return false;
    if (!q) return true;
    if (isExactItem) return isExactItem(row);
    return [
      row.item_name, row.master_name, row.current_code, row.item_code,
      row.item_id, row.reference_id, row.reference_no,
    ].some((value) => clean(value).toLowerCase().includes(q));
  });
};
